import React, { type CSSProperties, type ReactNode } from "react";
import { cx, useKitDisabled, useKitSize } from "./fieldContext";

/*
 * Auswahlfelder des Kits (Plan 2026-10-13-antd-ausbau, Stufe 6): Checkbox (+ Group) und Radio (+ Group, Button)
 * mit nativen Eingabefeldern. Schnittstelle wie Antd im genutzten Umfang; Ereignisse mit `target.checked` bzw.
 * `target.value`. Gewählter Zustand in der Auswahlfarbe des Themes (`--mq-kit-select`).
 */

type ChoiceValue = string | number | boolean;

/* Wie Antd ist `target.value` untypisiert: Gruppen tragen beliebige Werte. */
/* eslint-disable @typescript-eslint/no-explicit-any */
export type CheckboxChangeEvent = {
  target: { checked: boolean; value?: any; name?: string; id?: string };
  stopPropagation: () => void;
  preventDefault: () => void;
  nativeEvent: Event;
};

// ---------------------------------------------------------------- Checkbox

type CheckboxGroupContextValue = {
  value: unknown[];
  disabled?: boolean;
  name?: string;
  toggle: (value: unknown) => void;
};
const CheckboxGroupContext = React.createContext<CheckboxGroupContextValue | null>(null);

export interface CheckboxProps {
  checked?: boolean;
  defaultChecked?: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  value?: unknown;
  name?: string;
  id?: string;
  onChange?: (event: CheckboxChangeEvent) => void;
  onClick?: React.MouseEventHandler<HTMLElement>;
  children?: ReactNode;
  className?: string;
  rootClassName?: string;
  style?: CSSProperties;
  styles?: { root?: CSSProperties; icon?: CSSProperties; label?: CSSProperties };
  title?: string;
  autoFocus?: boolean;
  tabIndex?: number;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
}

function toEvent(event: React.ChangeEvent<HTMLInputElement>, value: unknown): CheckboxChangeEvent {
  return {
    target: { checked: event.target.checked, value, name: event.target.name || undefined, id: event.target.id || undefined },
    stopPropagation: () => event.stopPropagation(),
    preventDefault: () => event.preventDefault(),
    nativeEvent: event.nativeEvent,
  };
}

const CheckboxBase = React.forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { checked, defaultChecked, indeterminate, disabled, value, name, id, onChange, onClick, children, className, rootClassName, style, styles, title, autoFocus, tabIndex, ...aria },
  ref,
) {
  const group = React.useContext(CheckboxGroupContext);
  const [inner, setInner] = React.useState(Boolean(defaultChecked));
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);
  const mergedDisabled = useKitDisabled(disabled ?? group?.disabled);
  const isChecked = group ? group.value.some((item) => item === value) : checked ?? inner;
  React.useEffect(() => { if (inputRef.current) inputRef.current.indeterminate = Boolean(indeterminate); }, [indeterminate]);
  return (
    <label
      className={cx("ck-checkbox-wrapper", isChecked && "ck-checkbox-wrapper-checked", mergedDisabled && "ck-checkbox-wrapper-disabled", rootClassName, className)}
      style={{ ...style, ...styles?.root }}
      title={title}
      onClick={onClick}
    >
      <span className={cx("ck-checkbox", isChecked && "ck-checkbox-checked", indeterminate && "ck-checkbox-indeterminate", mergedDisabled && "ck-checkbox-disabled")} style={styles?.icon}>
        <input
          ref={inputRef}
          type="checkbox"
          className="ck-checkbox-input"
          checked={isChecked}
          disabled={mergedDisabled}
          name={name ?? group?.name}
          id={id}
          value={typeof value === "string" || typeof value === "number" ? value : undefined}
          autoFocus={autoFocus}
          tabIndex={tabIndex}
          aria-checked={indeterminate ? "mixed" : undefined}
          {...aria}
          onChange={(event) => {
            if (group) group.toggle(value);
            else if (checked === undefined) setInner(event.target.checked);
            onChange?.(toEvent(event, value));
          }}
        />
        <span className="ck-checkbox-inner" aria-hidden="true" />
      </span>
      {children != null && children !== false ? <span className="ck-checkbox-label" style={styles?.label}>{children}</span> : null}
    </label>
  );
});

export type ChoiceOption<V = ChoiceValue> = { label: ReactNode; value: V; disabled?: boolean; title?: string; className?: string; style?: CSSProperties };

function normalizeOptions<V>(options: ReadonlyArray<V | ChoiceOption<V>> | undefined): ChoiceOption<V>[] {
  return (options ?? []).map((option) =>
    typeof option === "object" && option !== null && "value" in (option as object)
      ? (option as ChoiceOption<V>)
      : { label: String(option), value: option as V },
  );
}

export interface CheckboxGroupProps<V = ChoiceValue> {
  options?: ReadonlyArray<V | ChoiceOption<V>>;
  value?: V[];
  defaultValue?: V[];
  onChange?: (values: V[]) => void;
  disabled?: boolean;
  name?: string;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

function CheckboxGroup<V = ChoiceValue>({ options, value, defaultValue, onChange, disabled, name, className, style, children }: CheckboxGroupProps<V>) {
  const [inner, setInner] = React.useState<V[]>(defaultValue ?? []);
  const current = value ?? inner;
  const list = normalizeOptions(options);
  const context: CheckboxGroupContextValue = {
    value: current as unknown[],
    disabled,
    name,
    toggle: (item) => {
      const has = current.some((entry) => entry === item);
      const next = has ? current.filter((entry) => entry !== item) : [...current, item as V];
      // Reihenfolge wie die Optionen (Antd), damit gespeicherte Werte stabil bleiben.
      const ordered = list.length ? list.map((option) => option.value).filter((entry) => next.includes(entry)) : next;
      if (value === undefined) setInner(ordered);
      onChange?.(ordered);
    },
  };
  return (
    <CheckboxGroupContext.Provider value={context}>
      <div className={cx("ck-checkbox-group", className)} style={style} role="group">
        {list.length
          ? list.map((option) => (
            <CheckboxBase key={String(option.value)} value={option.value} disabled={option.disabled} title={option.title} className={option.className} style={option.style}>
              {option.label}
            </CheckboxBase>
          ))
          : children}
      </div>
    </CheckboxGroupContext.Provider>
  );
}

/** Ankreuzfeld (Ersatz für Antd `Checkbox`) für Auswahl, Zustimmung und Optionen in Rückfragen. */
export const Checkbox = Object.assign(CheckboxBase, { Group: CheckboxGroup });

// ---------------------------------------------------------------- Radio

export type RadioChangeEvent = {
  target: { checked: boolean; value: any; name?: string };
  stopPropagation: () => void;
  preventDefault: () => void;
  nativeEvent: Event;
};

type RadioGroupContextValue = {
  value: unknown;
  disabled?: boolean;
  name: string;
  optionType: "default" | "button";
  buttonStyle: "outline" | "solid";
  size?: "small" | "middle" | "large";
  select: (value: unknown, event: React.ChangeEvent<HTMLInputElement>) => void;
};
/* eslint-enable @typescript-eslint/no-explicit-any */
const RadioGroupContext = React.createContext<RadioGroupContextValue | null>(null);

export interface RadioProps {
  value?: unknown;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  name?: string;
  id?: string;
  onChange?: (event: RadioChangeEvent) => void;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  title?: string;
  "aria-label"?: string;
}

function radioEvent(event: React.ChangeEvent<HTMLInputElement>, value: unknown): RadioChangeEvent {
  return {
    target: { checked: event.target.checked, value, name: event.target.name || undefined },
    stopPropagation: () => event.stopPropagation(),
    preventDefault: () => event.preventDefault(),
    nativeEvent: event.nativeEvent,
  };
}

function RadioInput({ value, checked, defaultChecked, disabled, name, id, onChange, children, className, style, title, button, ...aria }: RadioProps & { button: boolean }) {
  const group = React.useContext(RadioGroupContext);
  const [inner, setInner] = React.useState(Boolean(defaultChecked));
  const mergedDisabled = useKitDisabled(disabled ?? group?.disabled);
  const isChecked = group ? group.value === value : checked ?? inner;
  const asButton = button || group?.optionType === "button";
  const size = useKitSize(group?.size);
  const prefix = asButton ? "ck-radio-button" : "ck-radio";
  return (
    <label
      className={cx(
        `${prefix}-wrapper`,
        isChecked && `${prefix}-wrapper-checked`,
        mergedDisabled && `${prefix}-wrapper-disabled`,
        asButton && group?.buttonStyle === "solid" && "ck-radio-button-wrapper-solid",
        asButton && size === "small" && "ck-radio-button-wrapper-sm",
        asButton && size === "large" && "ck-radio-button-wrapper-lg",
        className,
      )}
      style={style}
      title={title}
    >
      <span className={cx(prefix, isChecked && `${prefix}-checked`, mergedDisabled && `${prefix}-disabled`)}>
        <input
          type="radio"
          className={`${prefix}-input`}
          checked={isChecked}
          disabled={mergedDisabled}
          name={name ?? group?.name}
          id={id}
          value={typeof value === "string" || typeof value === "number" ? value : undefined}
          {...aria}
          onChange={(event) => {
            if (group) group.select(value, event);
            else if (checked === undefined) setInner(event.target.checked);
            onChange?.(radioEvent(event, value));
          }}
        />
        {asButton ? null : <span className="ck-radio-inner" aria-hidden="true" />}
      </span>
      {children != null && children !== false ? <span className={asButton ? "ck-radio-button-label" : "ck-radio-label"}>{children}</span> : null}
    </label>
  );
}

function RadioBase(props: RadioProps) {
  return <RadioInput {...props} button={false} />;
}
function RadioButton(props: RadioProps) {
  return <RadioInput {...props} button />;
}

export interface RadioGroupProps<V = ChoiceValue> {
  options?: ReadonlyArray<V | ChoiceOption<V>>;
  value?: V | null;
  defaultValue?: V;
  onChange?: (event: RadioChangeEvent) => void;
  disabled?: boolean;
  name?: string;
  optionType?: "default" | "button";
  buttonStyle?: "outline" | "solid";
  size?: "small" | "middle" | "large";
  block?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

function RadioGroup<V = ChoiceValue>({ options, value, defaultValue, onChange, disabled, name, optionType = "default", buttonStyle = "outline", size, block, className, style, children, id, ...aria }: RadioGroupProps<V>) {
  const fallbackName = React.useId();
  const [inner, setInner] = React.useState<unknown>(defaultValue);
  const current = value !== undefined ? value : inner;
  const context: RadioGroupContextValue = {
    value: current,
    disabled,
    name: name ?? fallbackName,
    optionType,
    buttonStyle,
    size,
    select: (next, event) => {
      if (value === undefined) setInner(next);
      onChange?.(radioEvent(event, next));
    },
  };
  return (
    <RadioGroupContext.Provider value={context}>
      <div
        id={id}
        role="radiogroup"
        className={cx("ck-radio-group", optionType === "button" && "ck-radio-group-button", buttonStyle === "solid" && "ck-radio-group-solid", block && "ck-radio-group-block", className)}
        style={style}
        {...aria}
      >
        {options
          ? normalizeOptions(options).map((option) => (
            <RadioInput key={String(option.value)} value={option.value} disabled={option.disabled} title={option.title} className={option.className} style={option.style} button={optionType === "button"}>
              {option.label}
            </RadioInput>
          ))
          : children}
      </div>
    </RadioGroupContext.Provider>
  );
}

/** Optionsfeld (Ersatz für Antd `Radio`) samt `Radio.Group` und `Radio.Button`. */
export const Radio = Object.assign(RadioBase, { Group: RadioGroup, Button: RadioButton });
